import React from "react";

// Reusable visuals (lightweight SVG) used across topics

export function PayoffCall() {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-auto" aria-label="Call option payoff diagram">
      <defs>
        <linearGradient id="kh-call" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="20" y1="120" x2="300" y2="120" stroke="#cbd5e1" strokeWidth="1" />
      <line x1="160" y1="20" x2="160" y2="160" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 4" />
      <text x="20" y="170" fontSize="10" fontFamily="JetBrains Mono" fill="#475569">spot price →</text>
      <text x="165" y="170" fontSize="10" fontFamily="JetBrains Mono" fill="#0f172a">strike</text>

      {/* premium loss zone */}
      <line x1="20" y1="135" x2="160" y2="135" stroke="#ef4444" strokeWidth="2.5" />
      {/* profit slope */}
      <line x1="160" y1="135" x2="280" y2="40" stroke="#10b981" strokeWidth="2.5" />
      <path d="M 160 135 L 280 40 L 280 120 L 160 120 Z" fill="url(#kh-call)" />

      <text x="60" y="150" fontSize="10" fontFamily="JetBrains Mono" fill="#ef4444">−premium</text>
      <text x="220" y="50" fontSize="10" fontFamily="JetBrains Mono" fill="#10b981">+profit</text>
    </svg>
  );
}

export function PayoffPut() {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-auto" aria-label="Put option payoff diagram">
      <defs>
        <linearGradient id="kh-put" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="20" y1="120" x2="300" y2="120" stroke="#cbd5e1" strokeWidth="1" />
      <line x1="160" y1="20" x2="160" y2="160" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 4" />
      <text x="20" y="170" fontSize="10" fontFamily="JetBrains Mono" fill="#475569">spot price →</text>
      <text x="165" y="170" fontSize="10" fontFamily="JetBrains Mono" fill="#0f172a">strike</text>

      <line x1="40" y1="40" x2="160" y2="135" stroke="#10b981" strokeWidth="2.5" />
      <line x1="160" y1="135" x2="300" y2="135" stroke="#ef4444" strokeWidth="2.5" />
      <path d="M 40 40 L 160 135 L 40 135 Z" fill="url(#kh-put)" />

      <text x="50" y="55" fontSize="10" fontFamily="JetBrains Mono" fill="#10b981">+profit</text>
      <text x="200" y="150" fontSize="10" fontFamily="JetBrains Mono" fill="#ef4444">−premium</text>
    </svg>
  );
}

export function OptionChainViz() {
  const rows = [
    { strike: 25800, ceOI: "4.5L", ce: 240, pe: 60, peOI: "3.8L" },
    { strike: 26000, ceOI: "8.2L", ce: 120, pe: 100, peOI: "7.5L", atm: true },
    { strike: 26200, ceOI: "6.1L", ce: 60, pe: 180, peOI: "5.6L" },
  ];
  return (
    <div className="rounded-xl border border-[#e2e8f0] overflow-hidden">
      <div className="grid grid-cols-5 bg-[#f8fafc] text-[10px] font-mono font-bold text-[#475569] tracking-[0.15em] py-2 px-3">
        <div>CE OI</div>
        <div>CE ₹</div>
        <div className="text-center">STRIKE</div>
        <div className="text-right">PE ₹</div>
        <div className="text-right">PE OI</div>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          className={`grid grid-cols-5 items-center py-2.5 px-3 text-sm border-t border-[#e2e8f0] ${
            r.atm ? "bg-[#084392]/5" : "bg-white"
          }`}
        >
          <div className="font-mono text-[#475569]">{r.ceOI}</div>
          <div className="font-bold text-[#10b981]">{r.ce}</div>
          <div className={`text-center font-bold ${r.atm ? "text-[#084392]" : "text-[#0f172a]"}`}>
            {r.strike.toLocaleString("en-IN")}
            {r.atm && <span className="ml-1 text-[9px] font-mono">ATM</span>}
          </div>
          <div className="text-right font-bold text-[#ef4444]">{r.pe}</div>
          <div className="text-right font-mono text-[#475569]">{r.peOI}</div>
        </div>
      ))}
    </div>
  );
}

export function VIXMeter({ value = 13 }) {
  // 0-50 scale arc
  const pct = Math.min(1, value / 50);
  const angle = -90 + pct * 180;
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5">
      <div className="text-[10px] font-mono font-bold text-[#475569] tracking-[0.15em]">
        INDIA VIX
      </div>
      <svg viewBox="0 0 200 120" className="mt-3 w-full">
        <defs>
          <linearGradient id="vix-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        <path d="M 20 100 A 80 80 0 0 1 180 100" stroke="url(#vix-grad)" strokeWidth="12" fill="none" strokeLinecap="round" />
        <line
          x1="100"
          y1="100"
          x2={100 + 70 * Math.cos((angle * Math.PI) / 180)}
          y2={100 + 70 * Math.sin((angle * Math.PI) / 180)}
          stroke="#0f172a"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx="100" cy="100" r="6" fill="#0f172a" />
        <text x="100" y="118" textAnchor="middle" fontSize="22" fontWeight="900" fill="#0f172a" fontFamily="Roboto">
          {value}
        </text>
      </svg>
      <div className="grid grid-cols-3 text-[10px] font-mono mt-2">
        <span className="text-[#10b981]">CALM</span>
        <span className="text-center text-[#f59e0b]">MILD</span>
        <span className="text-right text-[#ef4444]">PANIC</span>
      </div>
    </div>
  );
}

export function Sparkline({ data, color = "#084392" }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = Math.max(1, max - min);
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * 200},${50 - ((v - min) / range) * 40}`)
    .join(" ");
  return (
    <svg viewBox="0 0 200 60" className="w-full h-14">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function GreeksGrid() {
  const greeks = [
    { sym: "Δ", name: "Delta", role: "Price sensitivity", color: "#084392" },
    { sym: "Γ", name: "Gamma", role: "Delta speed", color: "#10b981" },
    { sym: "Θ", name: "Theta", role: "Time decay", color: "#ef4444" },
    { sym: "ν", name: "Vega", role: "Volatility impact", color: "#f59e0b" },
    { sym: "ρ", name: "Rho", role: "Interest rate", color: "#475569" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {greeks.map((g, i) => (
        <div key={i} className="rounded-xl border border-[#e2e8f0] p-4 bg-white">
          <div className="font-heading text-3xl" style={{ color: g.color }}>
            {g.sym}
          </div>
          <div className="mt-1 font-bold">{g.name}</div>
          <div className="text-xs text-[#475569]">{g.role}</div>
        </div>
      ))}
    </div>
  );
}
